import { useState, useEffect } from "react";
import Modal from "@/components/common/Modal";
import { useApp } from "@/contexts/AppContext";
import { AnalyticsFiltersDrawer } from "@/features/analytics/components/AnalyticsFiltersDrawer";
import {
  cloneAnalyticsFilters,
  createEmptyAnalyticsFilters,
  countActiveAnalyticsFilters,
} from "@/features/analytics/utils/analytics";

const filtersToPredefinedFilter = (filters) => {
  const payload = {};
  for (const key of Object.keys(filters)) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      payload[key] = [...value];
    }
  }
  return payload;
};

const predefinedFilterToAnalyticsFilters = (predefinedFilter) => {
  const base = createEmptyAnalyticsFilters();
  if (!predefinedFilter || typeof predefinedFilter !== "object") return base;
  for (const key of Object.keys(base)) {
    const value = predefinedFilter[key];
    if (Array.isArray(value)) {
      base[key] = [...value];
    }
  }
  return base;
};

// The provider's scope is one JSON object: the device-filter groups the
// drawer edits, plus the publisher endpoints linked to the login (a plain
// list the drawer does not know about). Merge both on the way out.
const withProviderUuids = (predefined, providerUuids) =>
  providerUuids.length > 0
    ? { ...predefined, providerUuid: [...providerUuids] }
    : predefined;

const providerUuidsOf = (predefinedFilter) =>
  Array.isArray(predefinedFilter?.providerUuid)
    ? [...predefinedFilter.providerUuid]
    : [];

export default function EditUserModal({ isOpen, onClose, user }) {
  const {
    updateUser,
    campaigns,
    availableChannels,
    availableCategories,
    providers = [],
  } = useApp();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [predefinedFilters, setPredefinedFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [draftFilters, setDraftFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [providerUuids, setProviderUuids] = useState([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setRole(user.role || "user");
      setPredefinedFilters(
        predefinedFilterToAnalyticsFilters(user.predefinedFilter),
      );
      setProviderUuids(providerUuidsOf(user.predefinedFilter));
      setPassword("");
    }
  }, [user]);

  const resetForm = () => {
    if (user) {
      setUsername(user.username || "");
      setRole(user.role || "user");
      setPredefinedFilters(
        predefinedFilterToAnalyticsFilters(user.predefinedFilter),
      );
      setProviderUuids(providerUuidsOf(user.predefinedFilter));
    } else {
      setUsername("");
      setRole("user");
      setPredefinedFilters(createEmptyAnalyticsFilters());
      setProviderUuids([]);
    }
    setPassword("");
    setError("");
  };

  const handleSubmit = async () => {
    if (!username) {
      setError("Username is required");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const updates = {};
      if (username !== user.username) {
        updates.username = username;
      }
      if (password) {
        updates.password = password;
      }
      if (role !== user.role) {
        updates.role = role;
      }
      // Only send predefinedFilter if the user is (or will become) a provider —
      // for other roles it's irrelevant and only adds noise to the payload.
      if (role === "provider") {
        updates.predefinedFilter = withProviderUuids(
          filtersToPredefinedFilter(predefinedFilters),
          providerUuids,
        );
      } else if (user.predefinedFilter) {
        // Clearing scope when role moves away from provider.
        updates.predefinedFilter = null;
      }

      await updateUser(user.uuid, updates);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const activeScopeCount = countActiveAnalyticsFilters(predefinedFilters);

  const footer = (
    <>
      <button
        className="btn btn-secondary"
        type="button"
        onClick={() => {
          resetForm();
          onClose();
        }}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        className="btn btn-primary"
        type="button"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Updating..." : "Update User"}
      </button>
    </>
  );

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title="Edit User"
      footer={footer}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}
      <div className="form-group-modal">
        <label htmlFor="edit-username">Username *</label>
        <input
          id="edit-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter username"
          autoComplete="username"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="edit-password">
          New Password (leave blank to keep current)
        </label>
        <input
          id="edit-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter new password"
          autoComplete="new-password"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="edit-role">Role *</label>
        <select
          id="edit-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="provider">Provider</option>
        </select>
      </div>
      {role === "provider" ? (
        <div className="form-group-modal">
          <label>Predefined scope (provider sees only matching data)</label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setDraftFilters(cloneAnalyticsFilters(predefinedFilters));
                setIsFiltersOpen(true);
              }}
              disabled={loading}
            >
              Edit scope
            </button>
            <span
              style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}
            >
              {activeScopeCount > 0
                ? `${activeScopeCount} filter group(s) set`
                : "No scope set — provider will see nothing"}
            </span>
          </div>
        </div>
      ) : null}

      {role === "provider" ? (
        <div className="form-group-modal">
          <label>
            Publisher endpoints (what the Earnings page pays out on; the Devices
            list is limited to their traffic)
          </label>
          {providers.length === 0 ? (
            <span
              style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}
            >
              No publishers defined yet.
            </span>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.35rem",
                maxHeight: "12rem",
                overflowY: "auto",
              }}
            >
              {[
                ...providers,
                // An endpoint deleted since it was linked still has to be
                // shown, or it could never be unticked and would be saved back.
                ...providerUuids
                  .filter((id) => !providers.some((p) => p.uuid === id))
                  .map((id) => ({
                    uuid: id,
                    name: "Removed endpoint",
                    alias: id,
                  })),
              ].map((p) => (
                <label
                  key={p.uuid}
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                    fontWeight: 400,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={providerUuids.includes(p.uuid)}
                    onChange={(e) =>
                      setProviderUuids((prev) =>
                        e.target.checked
                          ? [...prev, p.uuid]
                          : prev.filter((id) => id !== p.uuid),
                      )
                    }
                    disabled={loading}
                  />
                  <span>
                    {p.name}
                    {p.alias ? (
                      <span style={{ color: "var(--text-secondary)" }}>
                        {" "}
                        ({p.alias})
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          )}
          <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            {providerUuids.length > 0
              ? `${providerUuids.length} endpoint(s) linked`
              : "None linked — the publisher will see no earnings"}
          </span>
        </div>
      ) : null}

      <AnalyticsFiltersDrawer
        isOpen={isFiltersOpen}
        value={draftFilters}
        campaigns={campaigns || []}
        channels={availableChannels || []}
        categories={availableCategories || []}
        onChange={setDraftFilters}
        onClose={() => setIsFiltersOpen(false)}
        onApply={() => {
          setPredefinedFilters(cloneAnalyticsFilters(draftFilters));
          setIsFiltersOpen(false);
        }}
        onReset={() => setDraftFilters(createEmptyAnalyticsFilters())}
      />
    </Modal>
  );
}
